import { TrendingUp, TrendingDown } from "lucide-react";
import clsx from "clsx";
import { GlassCard } from "./ui";

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length < 2) return null;
  const w = 96, h = 28;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const span = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / span) * h}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
      <circle
        cx={w}
        cy={h - ((data[data.length - 1] - min) / span) * h}
        r={2}
        fill={color}
      />
    </svg>
  );
}

export function KpiTile({
  label,
  value,
  deltaPct,
  spark,
  accent = "var(--ink)",
  critical,
  invertDelta,
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  deltaPct?: number | null;
  spark?: number[];
  accent?: string;
  critical?: boolean;
  invertDelta?: boolean; // for metrics where up is bad (critical, events)
  onClick?: () => void;
}) {
  const color = critical ? "var(--critical)" : accent;
  const hasDelta = deltaPct !== undefined && deltaPct !== null;
  // For "bad-when-up" metrics, a rise is red and a fall is green.
  const positive = hasDelta ? (invertDelta ? (deltaPct as number) < 0 : (deltaPct as number) >= 0) : true;
  const deltaColor = !hasDelta ? "var(--slate)" : positive ? "#2DD4BF" : "#EF4444";
  const Arrow = (deltaPct ?? 0) >= 0 ? TrendingUp : TrendingDown;

  return (
    <GlassCard
      className={clsx(
        "relative overflow-hidden transition",
        onClick && "cursor-pointer hover:border-aurora-cyan/40 hover:shadow-glow"
      )}
    >
      <div onClick={onClick}>
        <div className="section-tag">{label}</div>
        <div className="mt-1 flex items-end justify-between">
          <div className="text-3xl font-bold" style={{ color }}>{value}</div>
          {spark && spark.length > 1 && <Sparkline data={spark} color={critical ? "#EF4444" : accent} />}
        </div>
        {hasDelta && (
          <div className="mt-1 flex items-center gap-1 text-xs" style={{ color: deltaColor }}>
            <Arrow size={13} />
            <span className="mono">{Math.abs(deltaPct as number).toFixed(1)}%</span>
            <span className="text-slatey">vs prev</span>
          </div>
        )}
      </div>
      {critical && (
        <span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-critical animate-pulseDot" />
      )}
    </GlassCard>
  );
}
